/**
 * Two things the window chrome asks a surface to do.
 *
 * The desktop app puts **Meet now** and **New chat** in its title bar, above
 * every surface, and the dialogs they open belong to the chats and meetings
 * lists. `main.cpp` joins the two by hand, because a view reaching across the
 * tree to call a sibling is the thing that convention exists to prevent; this
 * is the browser's version of that join, and it is deliberately the smallest
 * one that works.
 *
 * An intent is *pending* rather than fired-and-forgotten, because the press
 * usually happens on another surface: New chat from Settings navigates first,
 * and the chats list is not mounted until after the click. So a page takes
 * whatever is waiting for it when it mounts, and subscribes for the presses
 * that arrive while it is open.
 */

export type ChromeIntent = 'new-chat' | 'new-meeting';

const listeners = new Set<(intent: ChromeIntent) => void>();
let pending: ChromeIntent | null = null;

export function requestChrome(intent: ChromeIntent): void {
  pending = intent;
  for (const listener of listeners) listener(intent);
}

/** The intent waiting for this surface, if it is the one that was asked for.
 *  Taking it clears it: an intent is a press, and a press happens once. */
export function takeChromeIntent(intent: ChromeIntent): boolean {
  if (pending !== intent) return false;
  pending = null;
  return true;
}

export function onChromeIntent(cb: (intent: ChromeIntent) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
